import { Grid, Paper, TextField } from '@material-ui/core';
import React from 'react';
import update from 'react-addons-update'; // ES6

import { createIPTrace, IIPTrace } from '~/src/dash/api/ip-trace';
import { AsyncButtonV2 } from '~/src/dash/components/button';
import { ModelTopBar } from '~/src/dash/components/model-topbar';
import { Panel, Panels } from '~/src/dash/publicaUI';
import { IStore } from '~/src/dash/reducers/store';
import { extractRouteParams, navigate, RouteIPTraceList } from '~/src/dash/router/routes';
import {
  IBaseState,
  IHistoryProps,
  IReduxActionProps,
  ISessionStateProps,
  IUserStateProps,
  IWithStylesProps,
} from '~/src/dash/types/props';
import { publicaColors } from '~/src/dash/utils/colors';
import { decorate } from '~/src/dash/utils/decorator';
import { routeName } from '~/src/dash/utils/routes';
import { hasErrors, IErrors, IValidator, validatorFactory } from '~/src/dash/utils/validator';
import { HeadingCategory } from '~/src/dash/views/component-view';
import { LoadingView } from '~/src/dash/views/loading-view';
import { SingleSelect } from '~/src/dash/views/singleselect-view';
import { SnackbarView } from '~/src/dash/views/snackbar-view';

interface IProps
  extends IWithStylesProps,
    IReduxActionProps,
    ISessionStateProps,
    IUserStateProps,
    IHistoryProps {
  ipTrace?: IIPTrace;
}

interface IState extends IBaseState {
  ipTrace?: IIPTrace;
  showLoadingWheel: boolean;
  snackbarMessage: string;
  snackbarError: boolean;
  errors: IErrors;
}

interface IIPTraceExistingRow {
  label: string;
  name: string;
  value?: string;
}

function checkIsValidIP(ip: string) {
  return ip.match(
    /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/,
  );
}

function checkIsValidEmail(email: string) {
  return email.match(/\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/);
}

const TimeFrameSelection = [
  {
    label: '1 Hour',
    value: '1',
  },
  {
    label: '6 Hour',
    value: '6',
  },
  {
    label: '12 Hour',
    value: '12',
  },
  {
    label: '24 Hour',
    value: '24',
  },
];

const validationSetting: IValidator = {
  IP(v: string): string {
    if (!v) {
      return 'Choose a IP';
    }
    if (!checkIsValidIP(v)) {
      return 'Not a valid IP';
    }
    return '';
  },
  Email(v: string): string {
    if (!v) {
      return 'Choose a Email';
    }
    if (!checkIsValidEmail(v)) {
      return 'Not a valid Email';
    }
    return '';
  },
};

export const IPTraceSettingForm = decorate(
  {
    redux: (state: IStore) => {
      return {
        user: state.user,
        session: state.session,
      };
    },
    styles: (theme: any) => ({
      paper: {
        ...theme.mixins.gutters(),
        paddingTop: theme.spacing.unit * 2,
        paddingBottom: theme.spacing.unit * 2,
        marginTop: theme.spacing.unit * 4,
        marginRight: 'auto',
        marginLeft: 'auto',
        maxWidth: '600px',
      },
    }),
    router: true,
  },
  class extends React.Component<IProps, IState> {
    state = {
      ipTrace: this.props.ipTrace,
      showLoadingWheel: false,
      snackbarMessage: '',
      snackbarError: false,
      errors: {} as IErrors,
    };

    createIPTrace = () => {
      const { ipTrace } = this.state;
      const { user } = this.props;
      if (!ipTrace) {
        return;
      }

      const errors = validatorFactory(validationSetting)(ipTrace);
      if (hasErrors(errors)) {
        this.setState(
          {
            errors,
            snackbarMessage: 'Please fix errors before saving IP Trace',
            snackbarError: true,
          },
          () => {
            setTimeout(this.snackbarClose, 6000);
          },
        );
        return;
      }

      this.setState(
        {
          showLoadingWheel: true,
        },
        () => {
          createIPTrace(
            {
              IP: ipTrace.IP,
              Email: ipTrace.Email,
              PublisherID: ipTrace.PublisherID,
              UserID: user.user.ID,
            },
            this.props.session.publisher.ID,
            ipTrace.TimeFrame || '1',
          ).then(
            (ipTrace: IIPTrace) => {
              this.setState(
                {
                  showLoadingWheel: false,
                  ipTrace,
                },
                () => {
                  setTimeout(this.gotoIPTraceList, 3000);
                },
              );
            },
            (err: Error) => {
              console.error(err);
              this.setState({
                showLoadingWheel: false,
                snackbarError: true,
                snackbarMessage:
                  'There seems to be an error creating the IP Trace: ' + err.toString(),
              });
            },
          );
        },
      );
    };

    handleChange = ({ target }: React.ChangeEvent<HTMLInputElement>) => {
      if (this.state.ipTrace) {
        this.setState({
          ipTrace: update(this.state.ipTrace, {
            $merge: {
              [target.name]: target.value,
            },
          }),
        });
      }
    };

    handleChangeSelect = (name: string, value: string | number) => {
      if (this.state.ipTrace) {
        this.setState({
          ipTrace: update(this.state.ipTrace, {
            $merge: {
              [name]: value,
            },
          }),
        });
      }
    };

    gotoIPTraceList = () => {
      navigate(
        RouteIPTraceList,
        extractRouteParams({
          session: this.props.session,
          user: this.props.user,
        }),
      );
    };

    snackbarClose = () => {
      this.setState({
        snackbarMessage: '',
      });
    };

    render() {
      const { classes, user, session, location } = this.props;
      const { pathname } = location;
      const { ipTrace, snackbarMessage, snackbarError } = this.state;
      const ipTraceAttributes: IIPTraceExistingRow[] = [
        { label: 'UUID', name: 'UUID', value: (ipTrace || {}).UUID },
        { label: 'Start Date', name: 'StartDate', value: (ipTrace || {}).StartDate },
        { label: 'IP', name: 'IP', value: (ipTrace || {}).IP },
        { label: 'Email', name: 'Email', value: (ipTrace || {}).Email },
      ];

      return (
        <Panels maxWidth vertical relative minWidthMain>
          <Panel
            largeHorizontalPadding
            verticalPadding
            style={{ borderBottom: `1px solid ${publicaColors.lightGray}` }}
          >
            <ModelTopBar
              title={routeName(pathname)}
              session={session}
              user={user}
              publisher={session.publisher}
              classes={classes}
              delegateElevate={true}
              currentPath={pathname}
              disableTitlePaddingLeft
            />
          </Panel>
          <Panel scroll largePadding>
            {ipTrace ? (
              <Paper
                classes={{
                  root: classes.paper,
                }}
              >
                <HeadingCategory>Create New IP Trace</HeadingCategory>
                <Grid
                  container
                  justify='space-between'
                  alignItems='center'
                  style={{ paddingBottom: 18 }}
                  spacing={2}
                >
                  {
                    // for existing IP Trace, user is not allow to change
                    !!ipTrace.ID &&
                      ipTraceAttributes.map((ipTraceAttribute: IIPTraceExistingRow) => (
                        <Grid item xs={8}>
                          <TextField
                            disabled
                            fullWidth
                            label={ipTraceAttribute.label}
                            name={ipTraceAttribute.name}
                            value={ipTraceAttribute.value}
                          />
                        </Grid>
                      ))
                  }
                  {
                    // for new IP Trace, user only needs to input IP, Email and
                    !ipTrace.ID && (
                      <>
                        <Grid item xs={8}>
                          <TextField
                            fullWidth
                            label='IP'
                            name='IP'
                            value={ipTrace.IP}
                            onChange={this.handleChange}
                            helperText={this.state.errors.IP}
                            error={!!this.state.errors.IP}
                          />
                        </Grid>
                        <Grid item xs={8}>
                          <TextField
                            fullWidth
                            label='Email'
                            name='Email'
                            value={ipTrace.Email}
                            onChange={this.handleChange}
                            helperText={this.state.errors.Email}
                            error={!!this.state.errors.Email}
                          />
                        </Grid>
                        <Grid item xs={8}>
                          <SingleSelect
                            label='Time Frame'
                            name='TimeFrame'
                            value={ipTrace.TimeFrame || ''}
                            onChange={this.handleChangeSelect}
                            items={TimeFrameSelection}
                          />
                        </Grid>
                        <Grid item xs={8}>
                          <div style={{ marginTop: '2rem', display: 'inline-block' }}>
                            <AsyncButtonV2
                              text='Save'
                              inprogress={this.state.showLoadingWheel}
                              onClick={this.createIPTrace}
                            />
                          </div>
                          <div
                            style={{
                              marginTop: '2rem',
                              marginLeft: '2rem',
                              display: 'inline-block',
                            }}
                          >
                            <AsyncButtonV2
                              text='Go To IP Trace List'
                              inprogress={false}
                              onClick={this.gotoIPTraceList}
                            />
                          </div>
                        </Grid>
                      </>
                    )
                  }
                </Grid>
                <SnackbarView
                  open={!!snackbarMessage}
                  error={!!snackbarError}
                  onClose={this.snackbarClose}
                >
                  {snackbarMessage}
                </SnackbarView>
              </Paper>
            ) : (
              <LoadingView active={true} text='Loading IP Trace...' />
            )}
          </Panel>
        </Panels>
      );
    }
  },
);
